/* eslint-disable max-len */
import {
  PersonType, ForeignNational, Role, ProcessRequestState, CaseState, EmployerAdminAssignment,
} from 'API';
import { useContext } from 'react';
import CaseContext from 'shared/contexts/CaseContext';
import CompanyContext from 'shared/contexts/CompanyContext';
import ProcessContext from 'shared/contexts/ProcessContext';
import ProfileContext from 'shared/contexts/ProfileContext';
import UserContext from 'shared/contexts/UserContext';

/**
 * Returns a function that determines whether the current user
 * can edit a field.
 */
const useCanEditField = (): (
field: ToggleableField, type?: ProfileType) => boolean => {
  const [userState] = useContext(UserContext);
  const [profile, , profileType] = useContext(ProfileContext);
  const [company] = useContext(CompanyContext);
  const [process] = useContext(ProcessContext);
  const [thisCase] = useContext(CaseContext);
  const { adminCompanies, role, person } = userState;
  let isAdminEM: boolean = false;
  const isAssignedToEM = process?.assignedToEmployer;
  if (person && person.adminCompanies) {
    isAdminEM = person.adminCompanies.items.some((value: EmployerAdminAssignment) => value.companyID === process?.companyID && value.employerID === person.id);
  }

  return (field: ToggleableField, type?: ProfileType): boolean => {
    if (!type || !field.fieldName) {
      return false;
    }

    if (type === 'case' && thisCase?.state !== CaseState.OPEN) {
      return false;
    }

    // APPROVED Process Requests cannot be edited
    if (type === 'processRequest' && process?.state === ProcessRequestState.APPROVED) {
      return false;
    }

    // ARCHIVED Process Requests cannot be edited
    if (type === 'processRequest' && process?.state === ProcessRequestState.ARCHIVED) {
      return false;
    }

    // Employers should not edit fields of a process request
    // unless it has been kicked back to them.
    if ((type === 'processRequest' && role === Role.EMPLOYER && process?.assignedToID !== person.id && !isAdminEM)
    || (role === Role.EMPLOYER && isAssignedToEM !== undefined && !isAssignedToEM)) {
      return false;
    }

    // Field should not be editable if a connection is present
    if (
      field.connectionField
      && ((profile && (profile as any)[field.connectionField])
        || (company && (company as any)[field.connectionField])
        || (process && (process as any)[field.connectionField]))
      && field.type !== 'file'
    ) {
      return false;
    }

    const isSelf = profile?.id === person.id;

    const isRelativeAdmin = profileType === PersonType.ForeignNational
      && ((profile as unknown) as ForeignNational)?.relativeAdmins?.includes(
        person.id,
      );

    const isCompanyAdmin = (company?.id && adminCompanies.includes(company.id))
      || (profile
        && profileType === PersonType.ForeignNational
        && ((profile as unknown) as ForeignNational).companyID
        && adminCompanies.includes(
          ((profile as unknown) as ForeignNational).companyID as string,
        ));

    const canEdit = Boolean(field.canEdit.includes(role)
      || (field.selfCanEdit && isSelf)
      || (field.relativeAdminsCanEdit && isRelativeAdmin)
      || (field.companyAdminsCanEdit && isCompanyAdmin));

    return canEdit;
  };
};

export default useCanEditField;
